import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Redirect, useHistory, useParams } from 'react-router-dom'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import DialogActions from '@material-ui/core/DialogActions'
import DialogTitle from '@material-ui/core/DialogTitle'
import LinearProgress from '@material-ui/core/LinearProgress'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import Typography from '@material-ui/core/Typography'
import Dialog from 'components/dialog'
import { useDialogTitleStyles } from 'components/dialog/use-dialog-title-styles'
import Error from 'components/error'
import { PageHeader } from 'components/page-header'
import { useAuth } from 'lib/auth'
import { useSnackbar, useSnackbarError, useSnackbarSuccess } from 'lib/snackbar'
import {
  useCancelCoachCampaign,
  useCoachCampaignResults,
  useDuplicateCoachCampaign,
} from './api-hooks'
import { ConversionBadge, StatusBadge } from './badges'
import ChatPreview from './chat-preview'
import { CAMPAIGNS_PATH } from './constants'
import {
  formatCampaignDate,
  formatRate,
  formatShortDate,
  studentName,
  toCampaignState,
} from './format'
import useStyles from './styles'
import SectionTitle from 'components/section-title'

const Stat = ({ value, label, caption, tone }) => {
  const classes = useStyles()
  return (
    <Box
      className={
        tone ? `${classes.statCard} ${classes[tone]}` : classes.statCard
      }
    >
      <Typography className={classes.statValue}>{value}</Typography>
      <Typography className={classes.statLabel}>{label}</Typography>
      {caption ? (
        <Typography className={classes.statCaption}>{caption}</Typography>
      ) : null}
    </Box>
  )
}

Stat.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  label: PropTypes.string.isRequired,
  caption: PropTypes.string,
  tone: PropTypes.oneOf(['directTone', 'influencedTone']),
}

const ResultsStats = ({ results }) => {
  const classes = useStyles()
  return (
    <>
      <Box className={classes.statsGrid}>
        <Stat value={results.recipientCount} label="Sent to" />
        <Stat
          value={results.readCount ?? '—'}
          label="Read"
          caption={
            results.readCount == null ? 'Couldn’t load read counts.' : undefined
          }
        />
        <Stat value={results.repliedCount} label="Replied" />
        <Stat value={results.linkVisitCount} label="Opened a link" />
      </Box>
      <Box
        className={classes.statsGrid}
        style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}
      >
        <Stat
          value={results.directBookingCount}
          label="Booked from link"
          caption="Booked using a card in this campaign."
          tone="directTone"
        />
        <Stat
          value={results.influencedBookingCount}
          label="Booked later"
          caption={`Booked with you within ${results.attributionWindowDays} days without using a card.`}
          tone="influencedTone"
        />
        <Stat
          value={formatRate(results.reactivationRate)}
          label="Reactivation rate"
          caption={
            results.reactivationEligibleCount > 0
              ? `${results.reactivatedCount} of ${results.reactivationEligibleCount} students with no lesson in ${results.reactivationLookbackDays} days booked again.`
              : `No one in this campaign had gone ${results.reactivationLookbackDays} days without a lesson.`
          }
        />
      </Box>
    </>
  )
}

ResultsStats.propTypes = {
  results: PropTypes.shape({
    recipientCount: PropTypes.number.isRequired,
    readCount: PropTypes.number,
    repliedCount: PropTypes.number.isRequired,
    linkVisitCount: PropTypes.number.isRequired,
    directBookingCount: PropTypes.number.isRequired,
    influencedBookingCount: PropTypes.number.isRequired,
    reactivationEligibleCount: PropTypes.number.isRequired,
    reactivatedCount: PropTypes.number.isRequired,
    reactivationRate: PropTypes.number,
    attributionWindowDays: PropTypes.number.isRequired,
    reactivationLookbackDays: PropTypes.number.isRequired,
  }).isRequired,
}

const RecipientsTable = ({ recipients }) => {
  const classes = useStyles()
  return (
    <Box style={{ overflowX: 'auto' }}>
      <Table size="small" className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell>Student</TableCell>
            <TableCell>Last lesson before campaign</TableCell>
            <TableCell>Opened a link</TableCell>
            <TableCell>Replied</TableCell>
            <TableCell>Booked</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {recipients.map(recipient => {
            const name = studentName(recipient.student)
            return (
              <TableRow key={recipient.id}>
                <TableCell>
                  {name}
                  {recipient.contactName && recipient.contactName !== name ? (
                    <Typography className={classes.statCaption}>
                      Sent to {recipient.contactName}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell>{formatShortDate(recipient.lastBookedAt)}</TableCell>
                <TableCell>{formatShortDate(recipient.visitedAt)}</TableCell>
                <TableCell>{formatShortDate(recipient.repliedAt)}</TableCell>
                <TableCell>
                  {recipient.conversionKind ? (
                    <ConversionBadge kind={recipient.conversionKind} />
                  ) : (
                    '—'
                  )}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </Box>
  )
}

RecipientsTable.propTypes = {
  recipients: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      contactName: PropTypes.string,
      lastBookedAt: PropTypes.string,
      visitedAt: PropTypes.string,
      repliedAt: PropTypes.string,
      conversionKind: PropTypes.oneOf(['DIRECT', 'INFLUENCED']),
      student: PropTypes.shape({
        firstName: PropTypes.string,
        lastName: PropTypes.string,
      }),
    }),
  ).isRequired,
}

export function CampaignResultsView({ campaign, busy, onDuplicate, onCancel }) {
  const classes = useStyles()
  const dialogClasses = useDialogTitleStyles()
  const [confirmCancel, setConfirmCancel] = useState(false)
  const preview = toCampaignState(campaign)
  const sent = campaign.status === 'SENT'

  const actions = (
    <Box display="flex" gridGap={8}>
      {campaign.cancelable ? (
        <Button
          variant="outlined"
          color="primary"
          className={classes.pillButton}
          disabled={busy}
          onClick={() => setConfirmCancel(true)}
        >
          Cancel Send
        </Button>
      ) : null}
      <Button
        variant="contained"
        color="primary"
        className={classes.pillButton}
        disabled={busy}
        onClick={onDuplicate}
      >
        Duplicate
      </Button>
    </Box>
  )

  return (
    <>
      <PageHeader
        title={campaign.name}
        crumbs={[{ label: 'Campaigns', to: CAMPAIGNS_PATH }]}
        actions={actions}
        fixedMobileActions
      />
      {busy ? <LinearProgress color="primary" /> : null}
      <Box className={classes.pageContent}>
        <Box display="flex" alignItems="center" gridGap={12}>
          <StatusBadge status={campaign.status} />
          <Typography className={classes.helperText}>
            {formatCampaignDate(campaign)}
          </Typography>
        </Box>

        {sent ? (
          <ResultsStats results={campaign.results} />
        ) : (
          <Box className={classes.card}>
            <Box className={classes.cardBody}>
              <Typography className={classes.helperText}>
                {campaign.status === 'SCHEDULED'
                  ? 'Results show here once the campaign sends.'
                  : 'This campaign was canceled. Duplicate it to send it again.'}
              </Typography>
            </Box>
          </Box>
        )}

        {sent && campaign.attributionEndsAt ? (
          <Typography className={classes.footnote}>
            “Booked later” counts bookings through{' '}
            {formatShortDate(campaign.attributionEndsAt)}. A booking that used a
            card only counts as “Booked from link.”
          </Typography>
        ) : null}

        <Box className={classes.card}>
          <Box className={classes.cardHeader}>
            <SectionTitle>Message</SectionTitle>
          </Box>
          <Box className={classes.cardBody}>
            <ChatPreview
              body={preview.body}
              offerings={preview.offerings}
              media={preview.media}
            />
          </Box>
        </Box>

        {sent && campaign.recipients.length > 0 ? (
          <Box className={classes.card}>
            <Box className={classes.cardHeader}>
              <SectionTitle>Students</SectionTitle>
              {campaign.recipients.length <
              (campaign.results?.recipientCount ?? 0) ? (
                <Typography className={classes.cardSubtitle}>
                  Showing {campaign.recipients.length.toLocaleString()} of{' '}
                  {campaign.results.recipientCount.toLocaleString()} students
                </Typography>
              ) : null}
            </Box>
            <RecipientsTable recipients={campaign.recipients} />
          </Box>
        ) : null}
      </Box>

      <Dialog
        isOpen={confirmCancel}
        onClose={() => setConfirmCancel(false)}
        showCloseButton
        fullWidth
        maxWidth="xs"
        classes={{ paper: dialogClasses.dialogPaper }}
        aria-labelledby="cancel-campaign-title"
      >
        {({ closeDialog }) => (
          <>
            <DialogTitle
              disableTypography
              classes={{ root: dialogClasses.titleRoot }}
            >
              <Typography
                id="cancel-campaign-title"
                component="h2"
                className={dialogClasses.title}
              >
                Cancel this campaign?
              </Typography>
              <Typography className={dialogClasses.bodyText}>
                It won’t be sent. You can duplicate it later to send it again.
              </Typography>
            </DialogTitle>
            <DialogActions className={dialogClasses.actions}>
              <Button color="primary" size="large" onClick={closeDialog}>
                Keep It
              </Button>
              <Button
                color="primary"
                variant="contained"
                size="large"
                disableElevation
                onClick={() => {
                  setConfirmCancel(false)
                  onCancel()
                }}
              >
                Cancel Send
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  )
}

CampaignResultsView.propTypes = {
  campaign: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    cancelable: PropTypes.bool,
    attributionEndsAt: PropTypes.string,
    results: PropTypes.object,
    recipients: PropTypes.array.isRequired,
  }).isRequired,
  busy: PropTypes.bool,
  onDuplicate: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
}

const CampaignResults = () => {
  const { id } = useParams()
  const history = useHistory()
  const { user } = useAuth()
  const hasPaidTier = Boolean(user?.coach?.hasPaidTierSubscription)
  const { enqueueSnackbar } = useSnackbar()
  const showError = useSnackbarError(enqueueSnackbar)
  const showSuccess = useSnackbarSuccess(enqueueSnackbar)
  const [busy, setBusy] = useState(false)
  const { data, loading, error, refetch } = useCoachCampaignResults(id, {
    skip: !hasPaidTier,
  })
  const [duplicateCampaign] = useDuplicateCoachCampaign()
  const [cancelCampaign] = useCancelCoachCampaign()

  if (loading) return <LinearProgress color="primary" />
  if (error) return <Error error={error} />
  const campaign = data?.coachCampaign
  if (!campaign) return hasPaidTier ? <Error statusCode={404} /> : null
  if (campaign.status === 'DRAFT') {
    return <Redirect to={`${CAMPAIGNS_PATH}/${id}/edit`} />
  }

  const run = async (mutate, key, onSuccess) => {
    setBusy(true)
    let response
    try {
      const { data: result } = await mutate({ variables: { id } })
      response = result?.[key]
      if (!response?.success) showError(response?.message)
    } catch (e) {
      showError()
    }
    setBusy(false)
    if (response?.success) onSuccess(response)
  }

  return (
    <CampaignResultsView
      campaign={campaign}
      busy={busy}
      onDuplicate={() =>
        run(duplicateCampaign, 'duplicateCoachCampaign', response => {
          showSuccess('Copied to a new draft')
          history.push(`${CAMPAIGNS_PATH}/${response.campaign.id}/edit`)
        })
      }
      onCancel={() =>
        run(cancelCampaign, 'cancelCoachCampaign', () => {
          showSuccess('Campaign canceled')
          refetch()
        })
      }
    />
  )
}

export default CampaignResults
